import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Form, Table } from "antd";
import {
  canPerformAction,
  StepKeysName,
  StepNames,
} from "../../../RaiseNoticeAdRequest/utils";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import { RaiseAdRequestState } from "../../../../store/types/raiseAdRequest";
import { useParams } from "react-router-dom";
import {
  createNewFolderForAdOnDAM,
  getAgencyQuotesFormDataAction,
  getNoticeEmailDataAction,
  getQuotationDataAction,
  patchAgencyQuotesAction,
} from "../../../../store/actions/raiseAdRequest";
import EmailForm from "../EmailForm";
import { NoticeAdComments } from "../NoticeAdComments";
import { openToast } from "../../../../Toasts";

interface RowData {
  key: string;
  publication?: string;
  edition?: string;
  issue?: string;
  language?: string;
  width?: string;
  height?: string;
  insertions?: string;
  netCost?: string;
  creativeFormat?: string;
  isSummary?: boolean;
}

const permittedRoles = ["admin", "product-admin"];
export const MarketingApprovalForm: FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    quoteDetailsFormData,
    quoationApprovalFormData,
    userRole,
    lastStepName,
  } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );

  const isStepAccessible = canPerformAction(
    userRole,
    permittedRoles,
    StepNames[2],
    lastStepName,
    quoationApprovalFormData.trashed_at
  );

  const { requestId, selectedProductId: product_id } = useParams<{
    requestId: string;
    selectedProductId: string;
  }>();
  const [rows, setRows] = useState([
    ...quoteDetailsFormData.data,
    ...quoteDetailsFormData.quote_pricing,
  ]);
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false);
  const [isFormValuesChanged, setIsFormValuesChanged] = useState<boolean>(
    false
  );

  const tableRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (quoteDetailsFormData?.data || quoteDetailsFormData?.quote_pricing) {
      setRows([
        ...(quoteDetailsFormData.data || []),
        ...(quoteDetailsFormData.quote_pricing || []),
      ]);
    }
  }, [quoteDetailsFormData]);

  useEffect(() => {
    if (quoteDetailsFormData?.emailContent) {
      form.setFieldsValue({
        emailContent: {
          ...quoteDetailsFormData.emailContent,
          to:
            quoteDetailsFormData.emailContent?.to &&
            quoteDetailsFormData.emailContent.to.trim() !== ""
              ? quoteDetailsFormData.emailContent.to
              : quoationApprovalFormData?.emailContent?.cc,
          cc:
            quoteDetailsFormData.emailContent?.to &&
            quoteDetailsFormData.emailContent.to.trim() !== ""
              ? quoteDetailsFormData.emailContent.cc
              : quoationApprovalFormData?.emailContent?.to
        },
      });
    }
  }, [quoteDetailsFormData, form, quoationApprovalFormData]);

  useEffect(() => {
    dispatch(getAgencyQuotesFormDataAction(requestId));
    dispatch(getNoticeEmailDataAction({ requestId, stage: 3 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isStepAccessible && !!userRole) {
      openToast("info", "You only have view access to this information.");
    }
  }, [isStepAccessible, userRole]);

  const handleActionClick = useCallback(
    (status) => {
      dispatch(
        patchAgencyQuotesAction({
          status,
          stage: "Marketing Approvals",
        })
      );
      if (status === "Rejected") {
        setIsCommentsDrawerOpen(true);
      }
    },
    [dispatch]
  );

  const columns = [
    { title: "Publication", dataIndex: "publication" },
    { title: "Edition", dataIndex: "edition" },
    { title: "Issue", dataIndex: "issue" },
    { title: "Language", dataIndex: "language" },
    { title: "Width(cm)", dataIndex: "width" },
    { title: "Height(cm)", dataIndex: "height" },
    { title: "Insertions", dataIndex: "insertions", width: "12rem" },
    { title: "Net Cost", dataIndex: "net_cost", width: "12rem" },
    { title: "Creative Format", dataIndex: "creative_format" },
  ];

  const handleMarketingApproval = useCallback(
    (values: any) => {
      if (!quoteDetailsFormData.folder_created) {
        dispatch(
          createNewFolderForAdOnDAM({
            name: `${quoationApprovalFormData.request_name}-${quoationApprovalFormData.request_type}`,
            product_id,
            requestId,
            shouldSendMail: true,
            emailContent: values,
            tableHtml: tableRef?.current?.innerHTML,
          })
        );
      } else {
        dispatch(
          patchAgencyQuotesAction({
            status: "Accepted",
            stage: "Ad Release",
            nextStepKey: StepKeysName.AdRelease,
            workspaceId: product_id,
            id: requestId,
            shouldSendMail: true,
            emailContent: values,
            tableHtml: tableRef?.current?.innerHTML,
          })
        );
      }
    },
    [
      dispatch,
      product_id,
      quoationApprovalFormData,
      quoteDetailsFormData,
      requestId,
    ]
  );

  return (
    <>
      <div className="layout-wrapper">
        {/* Scrollable Form */}
        <div className="layout-wrapper__body">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "2rem",
            }}
          >
            <div
              style={{
                fontSize: "1.6rem",
                fontWeight: 600,
                color: "#394455",
              }}
            >
              Quotation Details Received From Agency (View-only)
            </div>

            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 500,
                color: "#6810B6",
                cursor: "pointer",
                marginTop: "1rem",
              }}
              onClick={() => setIsCommentsDrawerOpen(true)}
            >
              + Add Comments
            </div>
          </div>
            <Table
              className="table-custom request-table marketing-approval-table"
              pagination={false}
              dataSource={rows}
              columns={columns}
              rowClassName={(record) => (record.isSummary ? "summary-row" : "")}
              style={{ margin: "0", marginTop: "2rem" }}
            />
          <div style={{ display: "none" }}>
            <div ref={tableRef}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "2rem",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    {columns.map((col) => (
                      <th
                        key={col.dataIndex}
                        style={{
                          background: "white",
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "left",
                          fontWeight: 600,
                        }}
                      >
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={index}
                      className={row.isSummary ? "summary-row" : ""}
                      style={{
                        backgroundColor: row.isSummary ? "#fff" : "white",
                      }}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.dataIndex}
                          style={{
                            border: !!row[col.dataIndex]
                              ? "1px solid #ddd"
                              : "",
                            padding: "8px",
                            fontSize: "12px",
                          }}
                        >
                          {row[col.dataIndex] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: "2rem",
              gap: "1rem",
            }}
          >
            <button
              className="btn btn--purple"
              disabled={
                quoteDetailsFormData.status === "Accepted" || !isStepAccessible
              }
              onClick={() => handleActionClick("Accepted")}
            >
              Approve
            </button>
            <button
              className="btn btn--outline-red"
              disabled={
                quoteDetailsFormData.status === "Rejected" || !isStepAccessible
              }
              onClick={() => handleActionClick("Rejected")}
            >
              Reject
            </button>
          </div>

          {quoteDetailsFormData.status === "Accepted" ? (
            <div
              style={{
                margin: "2rem 0",
                display: "flex",
                gap: "2rem",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  color: "#394455",
                }}
              >
                Proceed to Email Approval
              </div>
              <Form
                form={form}
                className="custom-form"
                onFinish={handleMarketingApproval}
                onValuesChange={() => setIsFormValuesChanged(true)}
              >
                <EmailForm
                  form={form}
                  fixedBody={quoteDetailsFormData?.emailContent?.fixedBody}
                  footerMessage={
                    quoteDetailsFormData?.emailContent?.footerMessage
                  }
                  subject={quoteDetailsFormData?.emailContent?.subject}
                  isUserPermitted={isStepAccessible}
                  tableHtml={tableRef?.current?.innerHTML}
                  isTable={true}
                />
              </Form>
            </div>
          ) : (
            <></>
          )}
        </div>

        {!!isStepAccessible ? (
          <div className="layout-wrapper__footer">
            <button
              disabled={
                quoteDetailsFormData.status === "Rejected" ||
                quoteDetailsFormData.status === "Pending"
              }
              className="btn btn--purple"
              onClick={() => form.submit()}
            >
              Submit for approval
            </button>
          </div>
        ) : (
          <></>
        )}

        <NoticeAdComments
          setIsCommentsDrawerOpen={setIsCommentsDrawerOpen}
          isCommentsDrawerOpen={isCommentsDrawerOpen}
        />
      </div>
    </>
  );
};
